import { createContext, useCallback, useContext, useState } from "react";

export type DocumentScrollContextPageScrollDelegate = (page: number) => void;

interface DocumentScrollContextProps {
  setPageScroll: (page: number) => void;

  addPageScrollDelegate: (delegate: DocumentScrollContextPageScrollDelegate) => void;
  removePageScrollDelegate: (delegate: DocumentScrollContextPageScrollDelegate) => void;
}

const DocumentScrollContext = createContext<DocumentScrollContextProps>({
  setPageScroll: () => {},
  addPageScrollDelegate: () => {},
  removePageScrollDelegate: () => {},
});

function DocumentScrollContextProvider({ page: pageProp, children }: { page?: number; children: React.ReactNode }) {
  const [delegates] = useState<Map<DocumentScrollContextPageScrollDelegate, true>>(
    new Map<DocumentScrollContextPageScrollDelegate, true>()
  );
  const addPageScrollDelegate = useCallback((delegate: DocumentScrollContextPageScrollDelegate) => {
    if (delegates.get(delegate)) return;
    delegates.set(delegate, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const removePageScrollDelegate = useCallback((delegate: DocumentScrollContextPageScrollDelegate) => {
    if (!delegates.get(delegate)) return;
    delegates.delete(delegate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setPageScroll = useCallback((page: number) => {
    for (const fn of Array.from(delegates.keys())) {
      fn(page);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DocumentScrollContext.Provider
      value={{
        setPageScroll,
        addPageScrollDelegate,
        removePageScrollDelegate,
      }}
    >
      {children}
    </DocumentScrollContext.Provider>
  );
}

function useDocumentScrollContext() {
  return useContext(DocumentScrollContext);
}

export { DocumentScrollContext, DocumentScrollContextProvider, useDocumentScrollContext };
